export type Locale = 'en' | 'es'

export const locales: { code: Locale; label: string; name: string }[] = [
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'es', label: 'ES', name: 'Español' },
]

type Copy = {
  nav: { links: string[]; book: string; openMenu: string; closeMenu: string; backToTop: string }
  hero: {
    badge: string
    titleLines: [string, string, string]
    subtitle: string
    cta: string
  }
  stats: { value: string; label: string }[]
  features: { eyebrow: string; titleLines: [string, string]; intro: string; cards: { title: string; description: string }[] }
  showcase: { eyebrow: string; title: string; caption: string; words: [string, string, string] }
  programs: {
    eyebrow: string
    title: string
    note: string
    rows: { day: string; name: string; level: string; time: string }[]
  }
  community: {
    eyebrow: string
    titleLines: [string, string]
    body: string
    stats: { value: string; label: string }[]
  }
  cta: { titleLines: [string, string]; body: string; button: string }
  footer: {
    tagline: string
    explore: string
    contact: string
    booking: string
    rights: string
    motto: string
  }
  language: { label: string }
}

export const translations: Record<Locale, Copy> = {
  en: {
    nav: {
      links: ['Home', 'Courts', 'Programs', 'Community', 'Contact'],
      book: 'Book a Court',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      backToTop: 'Pulse Padel Hub — back to top',
    },
    hero: {
      badge: '5.0 on Google Reviews',
      titleLines: ['Play today.', 'Play again.', 'Play Pulse.'],
      subtitle:
        "North Miami's premier padel destination — 6 courts, free parking, extended hours.",
      cta: 'Book a Court',
    },
    stats: [
      { value: '6', label: 'Pro Courts' },
      { value: 'Free', label: 'On-Site Parking' },
      { value: 'Late', label: 'Open Until Late' },
      { value: 'All', label: 'Skill Levels' },
    ],
    features: {
      eyebrow: 'The Club',
      titleLines: ['Built for players', 'who never stop'],
      intro:
        'Six panoramic courts, floodlit late into the night, and a crowd that makes every match feel like an event.',
      cards: [
        {
          title: 'Six Pro Courts',
          description:
            'Panoramic, pro-grade glass courts under stadium LED lighting — built for night play.',
        },
        {
          title: 'Free On-Site Parking',
          description:
            'Pull up, park steps from the door, and be warming up in under two minutes. No meters, no hassle.',
        },
        {
          title: 'Vibrant Community',
          description:
            'Open plays, socials and leagues that mix you in with players at your level — every single week.',
        },
        {
          title: 'All Ages & Levels',
          description:
            'From first-time beginners to 5.0+ competitors and kids clinics on the weekends. Everyone plays.',
        },
      ],
    },
    showcase: {
      eyebrow: 'The Pulse Object',
      title: 'Engineered for night play',
      caption:
        'Neon-lit glass, floodlit courts and a room that hums until close. This is what a session at Pulse feels like.',
      words: ['Spin', 'Power', 'Play'],
    },
    programs: {
      eyebrow: 'Every Week',
      title: 'Weekly Programs',
      note: 'Drop into an open play, level up in a clinic, or bring the kids on Saturday. Spots are first come, first served.',
      rows: [
        { day: 'Mondays', name: 'Beginner Open', level: '0.5 – 2.0', time: '7:30 PM' },
        { day: 'Tuesdays', name: 'Intermediate Open', level: '2.0 – 3.5', time: '8:30 PM' },
        { day: 'Thursdays', name: 'Advanced Open', level: '3.5 – 5.0+', time: '7:30 PM' },
        { day: 'Saturdays', name: 'Kids Clinics', level: 'Ages 6 – 14', time: '11:00 AM – 12:30 PM' },
        { day: 'Sundays', name: 'Open Play', level: 'All levels', time: 'All day' },
      ],
    },
    community: {
      eyebrow: 'Community',
      titleLines: ['More than a club —', 'a community'],
      body: 'Pulse is where North Miami comes to play. Post-match hangs, mixers, leagues and a group chat that never sleeps — you arrive as a player and leave with a crew.',
      stats: [
        { value: '1,600+', label: 'Instagram Community' },
        { value: '5.0★', label: 'Google Rating' },
      ],
    },
    cta: {
      titleLines: ['Ready to play?', 'Book your court now'],
      body: 'Courts go fast on weeknights. Grab your slot in seconds on Playtomic.',
      button: 'Book a Court',
    },
    footer: {
      tagline:
        "North Miami's premier padel destination. Six courts, free parking, extended hours and a community that plays every night.",
      explore: 'Explore',
      contact: 'Contact',
      booking: 'Book on Playtomic',
      rights: 'All rights reserved.',
      motto: 'Play today. Play again. Play Pulse.',
    },
    language: { label: 'Language' },
  },

  es: {
    nav: {
      links: ['Inicio', 'Canchas', 'Programas', 'Comunidad', 'Contacto'],
      book: 'Reservar Cancha',
      openMenu: 'Abrir menú',
      closeMenu: 'Cerrar menú',
      backToTop: 'Pulse Padel Hub — volver arriba',
    },
    hero: {
      badge: '5.0 en Google Reviews',
      titleLines: ['Juega hoy.', 'Juega otra vez.', 'Juega Pulse.'],
      subtitle:
        'El destino de pádel número uno de North Miami — 6 canchas, parking gratis, horario extendido.',
      cta: 'Reservar Cancha',
    },
    stats: [
      { value: '6', label: 'Canchas Pro' },
      { value: 'Gratis', label: 'Parking en el Club' },
      { value: 'Tarde', label: 'Abierto Hasta Tarde' },
      { value: 'Todos', label: 'Los Niveles' },
    ],
    features: {
      eyebrow: 'El Club',
      titleLines: ['Hecho para quienes', 'nunca paran de jugar'],
      intro:
        'Seis canchas panorámicas, iluminadas hasta bien entrada la noche, y un ambiente que convierte cada partido en un evento.',
      cards: [
        {
          title: 'Seis Canchas Pro',
          description:
            'Canchas panorámicas de cristal de nivel profesional con iluminación LED — pensadas para jugar de noche.',
        },
        {
          title: 'Parking Gratis',
          description:
            'Llega, estaciona a unos pasos de la puerta y estarás calentando en menos de dos minutos. Sin parquímetros.',
        },
        {
          title: 'Comunidad Vibrante',
          description:
            'Open plays, socials y ligas que te mezclan con jugadores de tu nivel — todas las semanas.',
        },
        {
          title: 'Todas las Edades y Niveles',
          description:
            'Desde principiantes absolutos hasta competidores 5.0+ y clínicas para niños los fines de semana.',
        },
      ],
    },
    showcase: {
      eyebrow: 'El Objeto Pulse',
      title: 'Diseñado para el juego nocturno',
      caption:
        'Cristal iluminado en neón, canchas con reflectores y un ambiente que vibra hasta el cierre. Así se siente una sesión en Pulse.',
      words: ['Efecto', 'Potencia', 'Juego'],
    },
    programs: {
      eyebrow: 'Cada Semana',
      title: 'Programas Semanales',
      note: 'Súmate a un open play, mejora en una clínica o trae a los niños el sábado. Los cupos son por orden de llegada.',
      rows: [
        { day: 'Lunes', name: 'Open Principiantes', level: '0.5 – 2.0', time: '7:30 PM' },
        { day: 'Martes', name: 'Open Intermedios', level: '2.0 – 3.5', time: '8:30 PM' },
        { day: 'Jueves', name: 'Open Avanzados', level: '3.5 – 5.0+', time: '7:30 PM' },
        { day: 'Sábados', name: 'Clínicas para Niños', level: '6 – 14 años', time: '11:00 AM – 12:30 PM' },
        { day: 'Domingos', name: 'Open Play', level: 'Todos los niveles', time: 'Todo el día' },
      ],
    },
    community: {
      eyebrow: 'Comunidad',
      titleLines: ['Más que un club —', 'una comunidad'],
      body: 'Pulse es donde North Miami viene a jugar. Charlas después del partido, mixers, ligas y un grupo que nunca duerme — llegas como jugador y te vas con un equipo.',
      stats: [
        { value: '1,600+', label: 'Comunidad en Instagram' },
        { value: '5.0★', label: 'Calificación Google' },
      ],
    },
    cta: {
      titleLines: ['¿Listo para jugar?', 'Reserva tu cancha ahora'],
      body: 'Las canchas vuelan entre semana. Consigue tu horario en segundos por Playtomic.',
      button: 'Reservar Cancha',
    },
    footer: {
      tagline:
        'El destino de pádel número uno de North Miami. Seis canchas, parking gratis, horario extendido y una comunidad que juega cada noche.',
      explore: 'Explorar',
      contact: 'Contacto',
      booking: 'Reserva en Playtomic',
      rights: 'Todos los derechos reservados.',
      motto: 'Juega hoy. Juega otra vez. Juega Pulse.',
    },
    language: { label: 'Idioma' },
  },
}
